import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { useAppConfig } from "../../../AppConfigProvider";
import {
  ActivityProgressAnalytics,
  CourseProgressAnalytics,
  ModuleProgressAnalytics,
  ObjectiveProgressAnalytics,
  ProgressDetails,
  StudentDetailedAnalytics,
} from "../../../graphql/generated/graphql";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";

interface StudentDetailProps {
  studentDetailedAnalyticsData: StudentDetailedAnalytics | null;
  onBackToStudents: () => void;
  loading: boolean;
}

// Utility functions
const formatTime = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

const hasEngagement = (
  item: CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics
): boolean => {
  return (
    (item.success_points && item.success_points > 0) ||
    (item.confusion_points && item.confusion_points > 0) ||
    (item.questions_asked && item.questions_asked > 0) ||
    (item.active_interaction_count && item.active_interaction_count > 0) ||
    (item.distractions && item.distractions > 0) ||
    (item.completion_percentage && item.completion_percentage > 0) ||
    (item.total_time_taken && item.total_time_taken > 0)
  );
};

const sortByEngagement = <
  T extends CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics,
>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    const aActive = hasEngagement(a);
    const bActive = hasEngagement(b);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case "completed":
      return "#10B981"; // green
    case "inProgress":
      return "#3B82F6"; // blue
    case "notStarted":
      return "#6B7280"; // gray
    default:
      return "#6B7280";
  }
};

const getSentimentColor = (sentiment: string): string => {
  switch (sentiment?.toLowerCase()) {
    case "positive":
      return "#10B981"; // green
    case "negative":
      return "#EF4444"; // red
    case "neutral":
    default:
      return "#6B7280"; // gray
  }
};

// Metric badge component
const MetricBadge: React.FC<{
  label: string;
  value: string | number;
  color: string;
}> = ({ label, value, color }) => (
  <View style={[styles.metricBadge, { backgroundColor: color + "20", borderColor: color }]}>
    <CustomText text={`${label}: ${value}`} size="xs" style={{ color }} useTranslationText={false} />
  </View>
);

// Progress bar component
const ProgressBar: React.FC<{
  percentage: number;
  color?: string;
}> = ({ percentage, color = "#3B82F6" }) => (
  <View style={styles.progressBarContainer}>
    <View
      style={[
        styles.progressBarFill,
        {
          width: `${Math.min(percentage, 100)}%`,
          backgroundColor: color,
        },
      ]}
    />
  </View>
);

// Drill-down panel component
const DrillDownPanel: React.FC<{
  label: string;
  item: CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics;
  type: "course" | "module" | "activity" | "objective";
  children: React.ReactNode;
  defaultOpen?: boolean;
  level?: number;
  inactive?: boolean;
}> = ({ label, item, type, children, defaultOpen = false, level = 0, inactive = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const indentStyle = { marginLeft: level * 16 };
  const bgColor = level === 0 ? "#FFFFFF" : level === 1 ? "#F9FAFB" : level === 2 ? "#F3F4F6" : "#E5E7EB";

  const getBadge = () => {
    if (type === "objective" && item.status) {
      return (
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(item.status) + "20", borderColor: getStatusColor(item.status) },
          ]}>
          <CustomText
            text={
              item.status === "inProgress" ? "In Progress" : item.status === "completed" ? "Completed" : "Not Started"
            }
            size="xs"
            style={{ color: getStatusColor(item.status) }}
            useTranslationText={false}
          />
        </View>
      );
    } else if (item.sentiment) {
      return (
        <View
          style={[
            styles.statusBadge,
            {
              backgroundColor: getSentimentColor(item.sentiment) + "20",
              borderColor: getSentimentColor(item.sentiment),
            },
          ]}>
          <CustomText
            text={item.sentiment}
            size="xs"
            style={{ color: getSentimentColor(item.sentiment) }}
            useTranslationText={false}
          />
        </View>
      );
    }
    return null;
  };

  const completionPercentage = item.completion_percentage
    ? item.completion_percentage > 1
      ? item.completion_percentage
      : item.completion_percentage * 100
    : 0;

  return (
    <View style={[styles.drillDownPanel, indentStyle, { backgroundColor: bgColor, opacity: inactive ? 0.6 : 1 }]}>
      <TouchableOpacity style={styles.panelHeader} onPress={() => !inactive && setOpen(!open)} disabled={inactive}>
        <View style={styles.panelHeaderLeft}>
          <CustomText text={open ? "▼" : "▶"} size="s" style={styles.expandIcon} useTranslationText={false} />
          <CustomText text={label} size="m" weight="bold" useTranslationText={false} />
          {getBadge()}
        </View>
      </TouchableOpacity>

      {type !== "objective" && completionPercentage > 0 && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <CustomText text="Completion" size="xs" useTranslationText={false} />
            <CustomText text={formatPercentage(completionPercentage)} size="xs" useTranslationText={false} />
          </View>
          <ProgressBar percentage={completionPercentage} />
        </View>
      )}

      <View style={styles.metricsRow}>
        <MetricBadge label="Success" value={item.success_points || 0} color="#10B981" />
        <MetricBadge label="Confusion" value={item.confusion_points || 0} color="#EF4444" />
        <MetricBadge label="Questions" value={item.questions_asked || 0} color="#3B82F6" />
        <MetricBadge label="Interactions" value={item.active_interaction_count || 0} color="#8B5CF6" />
        <MetricBadge label="Distractions" value={item.distractions || 0} color="#F59E0B" />
        <MetricBadge label="Time" value={formatTime(item.total_time_taken || 0)} color="#6B7280" />
      </View>

      {open && <View style={styles.panelContent}>{children}</View>}
    </View>
  );
};

// Enhanced Progress details component matching AnalyticsPort exactly
const ProgressDetailsBlock: React.FC<{
  details?: ProgressDetails;
}> = ({ details }) => {
  // Use real data from GraphQL - no mock data
  const progressData = details;

  if (!progressData) return null;

  // Helper to get sentiment color for badges
  const getSentimentBadgeColor = (sentiment: string | null | undefined) => {
    if (!sentiment) return "#6B7280";

    const sentimentLower = sentiment.toLowerCase();
    if (sentimentLower.includes("positive")) return "#10B981";
    if (sentimentLower.includes("negative")) return "#EF4444";
    if (sentimentLower.includes("neutral")) return "#6B7280";
    if (sentimentLower.includes("confused")) return "#F59E0B";
    if (sentimentLower.includes("excited")) return "#3B82F6";

    return "#6B7280";
  };

  return (
    <View style={styles.progressDetailsContainer}>
      {/* Header with Level badge */}
      <View style={styles.progressDetailsHeader}>
        <CustomText text="Progress Details" size="l" weight="bold" useTranslationText={false} />
        {progressData.level && (
          <View style={[styles.levelBadge, { backgroundColor: "#3B82F6" + "20", borderColor: "#3B82F6" }]}>
            <CustomText
              text={`Level: ${progressData.level}`}
              size="xs"
              style={{ color: "#3B82F6" }}
              weight="bold"
              useTranslationText={false}
            />
          </View>
        )}
      </View>

      <CustomSpacing type="vertical" size="m" />

      {/* Summary section */}
      {progressData.summary && (
        <View style={styles.summarySection}>
          <CustomText text="Summary" size="m" weight="bold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={progressData.summary} size="s" useTranslationText={false} />
        </View>
      )}

      <CustomSpacing type="vertical" size="m" />

      {/* Analytics metrics section - two column layout */}
      <View style={styles.analyticsMetricsSection}>
        <View style={styles.analyticsColumn}>
          {/* Sentiment and Originality badges */}
          <View style={styles.badgesRow}>
            {progressData.sentiment_analysis && (
              <View
                style={[
                  styles.analyticsBadge,
                  {
                    backgroundColor: getSentimentBadgeColor(progressData.sentiment_analysis) + "20",
                    borderColor: getSentimentBadgeColor(progressData.sentiment_analysis),
                  },
                ]}>
                <CustomText
                  text="Sentiment:"
                  size="xs"
                  weight="bold"
                  style={{ color: getSentimentBadgeColor(progressData.sentiment_analysis) }}
                  useTranslationText={false}
                />
                <CustomText
                  text={` ${progressData.sentiment_analysis}`}
                  size="xs"
                  style={{ color: getSentimentBadgeColor(progressData.sentiment_analysis) }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.originality_analysis && (
              <View style={[styles.originalityBadge, { backgroundColor: "#8B5CF6" + "20", borderColor: "#8B5CF6" }]}>
                <CustomText
                  text="Originality:"
                  size="xs"
                  weight="bold"
                  style={{ color: "#8B5CF6" }}
                  useTranslationText={false}
                />
                <CustomText
                  text={progressData.originality_analysis}
                  size="xs"
                  style={{ color: "#8B5CF6", flexShrink: 1 }}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>

          {/* Message counts */}
          <View style={styles.badgesRow}>
            {progressData.questions_asked !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#3B82F6" + "20", borderColor: "#3B82F6" }]}>
                <CustomText
                  text={`Questions Asked: ${progressData.questions_asked}`}
                  size="xs"
                  style={{ color: "#3B82F6" }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.student_messages_count !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#6366F1" + "20", borderColor: "#6366F1" }]}>
                <CustomText
                  text={`Student Msgs: ${progressData.student_messages_count}`}
                  size="xs"
                  style={{ color: "#6366F1" }}
                  useTranslationText={false}
                />
              </View>
            )}
            {progressData.ai_messages_count !== undefined && (
              <View style={[styles.analyticsBadge, { backgroundColor: "#8B5CF6" + "20", borderColor: "#8B5CF6" }]}>
                <CustomText
                  text={`AI Msgs: ${progressData.ai_messages_count}`}
                  size="xs"
                  style={{ color: "#8B5CF6" }}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>
        </View>

        <View style={styles.analyticsColumn}>
          {/* Response times */}
          {progressData.average_student_response_time !== undefined && (
            <View style={styles.metricRow}>
              <CustomText
                text="Avg Student Response:"
                size="s"
                style={{ color: "#6B7280" }}
                useTranslationText={false}
              />
              <CustomText
                text={`${Math.round(progressData.average_student_response_time)}s`}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
          {progressData.average_ai_response_time !== undefined && (
            <View style={styles.metricRow}>
              <CustomText text="Avg AI Response:" size="s" style={{ color: "#6B7280" }} useTranslationText={false} />
              <CustomText
                text={`${Math.round(progressData.average_ai_response_time)}s`}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
          {progressData.total_time_taken !== undefined && (
            <View style={styles.metricRow}>
              <CustomText text="Total Time Taken:" size="s" style={{ color: "#6B7280" }} useTranslationText={false} />
              <CustomText
                text={formatTime(progressData.total_time_taken)}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Three-column points and recommendations section */}
      <View style={styles.pointsGrid}>
        <View style={[styles.pointsSection, { backgroundColor: "#10B981" + "10", borderColor: "#10B981" + "40" }]}>
          <View style={styles.pointsHeader}>
            <View style={[styles.pointsDot, { backgroundColor: "#10B981" }]} />
            <CustomText
              text="Success Points"
              size="s"
              weight="bold"
              style={{ color: "#10B981" }}
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.success_points && progressData.success_points.length > 0 ? (
            progressData.success_points.map((point, i) => (
              <View key={i} style={styles.pointItem}>
                <View style={[styles.pointBullet, { backgroundColor: "#10B981" }]} />
                <CustomText text={point} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText
              text="None recorded"
              size="xs"
              style={{ color: "#6B7280", fontStyle: "italic" }}
              useTranslationText={false}
            />
          )}
        </View>

        <View style={[styles.pointsSection, { backgroundColor: "#EF4444" + "10", borderColor: "#EF4444" + "40" }]}>
          <View style={styles.pointsHeader}>
            <View style={[styles.pointsDot, { backgroundColor: "#EF4444" }]} />
            <CustomText
              text="Confusion Points"
              size="s"
              weight="bold"
              style={{ color: "#EF4444" }}
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.confusion_points && progressData.confusion_points.length > 0 ? (
            progressData.confusion_points.map((point, i) => (
              <View key={i} style={styles.pointItem}>
                <View style={[styles.pointBullet, { backgroundColor: "#EF4444" }]} />
                <CustomText text={point} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText
              text="None recorded"
              size="xs"
              style={{ color: "#6B7280", fontStyle: "italic" }}
              useTranslationText={false}
            />
          )}
        </View>

        <View style={[styles.pointsSection, { backgroundColor: "#3B82F6" + "10", borderColor: "#3B82F6" + "40" }]}>
          <View style={styles.pointsHeader}>
            <View style={[styles.pointsDot, { backgroundColor: "#3B82F6" }]} />
            <CustomText
              text="Recommendations"
              size="s"
              weight="bold"
              style={{ color: "#3B82F6" }}
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="xs" />
          {progressData.further_recommendations && progressData.further_recommendations.length > 0 ? (
            progressData.further_recommendations.map((rec, i) => (
              <View key={i} style={styles.pointItem}>
                <View style={[styles.pointBullet, { backgroundColor: "#3B82F6" }]} />
                <CustomText text={rec} size="xs" style={{ color: "#374151", flex: 1 }} useTranslationText={false} />
              </View>
            ))
          ) : (
            <CustomText
              text="None provided"
              size="xs"
              style={{ color: "#6B7280", fontStyle: "italic" }}
              useTranslationText={false}
            />
          )}
        </View>
      </View>
    </View>
  );
};

const StudentDetail: React.FC<StudentDetailProps> = ({
  studentDetailedAnalyticsData,
  onBackToStudents,
  loading: externalLoading,
}) => {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const loading = externalLoading;

  if (loading) {
    return (
      <View style={styles.container}>
        <CustomText text="Loading student details..." size="m" useTranslationText={false} />
      </View>
    );
  }

  if (!studentDetailedAnalyticsData) {
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.backButton} onPress={onBackToStudents}>
          <CustomText text="← Back to Students" size="m" useTranslationText={false} />
        </TouchableOpacity>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="No analytics data found." size="m" useTranslationText={false} />
      </View>
    );
  }

  // Drill-down rendering functions
  const renderObjectives = (objectives?: ObjectiveProgressAnalytics[]) => {
    if (!objectives || objectives.length === 0) {
      return <CustomText text="No objectives found." size="s" useTranslationText={false} />;
    }
    const sorted = sortByEngagement(objectives);

    return (
      <View>
        {sorted.map((objective) => {
          return (
            <DrillDownPanel
              key={objective.id}
              label={objective.label}
              item={objective}
              type="objective"
              level={3}
              defaultOpen={hasEngagement(objective)} // Auto-open if has engagement
              inactive={!hasEngagement(objective)}>
              <ProgressDetailsBlock details={objective.progress_details} />
            </DrillDownPanel>
          );
        })}
      </View>
    );
  };

  const renderActivities = (activities?: ActivityProgressAnalytics[]) => {
    if (!activities || activities.length === 0) {
      return <CustomText text="No activities found." size="s" useTranslationText={false} />;
    }

    const sorted = sortByEngagement(activities);
    return (
      <View>
        {sorted.map((activity) => (
          <DrillDownPanel
            key={activity.id}
            label={activity.label}
            item={activity}
            type="activity"
            level={2}
            defaultOpen={hasEngagement(activity)} // Auto-open if has engagement
            inactive={!hasEngagement(activity)}>
            {activity.details?.objectives &&
              activity.details.objectives.length > 0 &&
              renderObjectives(activity.details.objectives)}
          </DrillDownPanel>
        ))}
      </View>
    );
  };

  const renderModules = (modules?: ModuleProgressAnalytics[]) => {
    if (!modules || modules.length === 0) {
      return <CustomText text="No modules found." size="s" useTranslationText={false} />;
    }

    const sorted = sortByEngagement(modules);
    return (
      <View>
        {sorted.map((module) => {
          const moduleActivities = module.details?.activities || [];

          return (
            <DrillDownPanel
              key={module.id}
              label={module.label}
              item={module}
              type="module"
              level={1}
              defaultOpen={hasEngagement(module)} // Auto-open if has engagement
              inactive={!hasEngagement(module)}>
              {moduleActivities.length > 0 ? (
                renderActivities(moduleActivities)
              ) : (
                <CustomText text="No activities found for this module." size="s" useTranslationText={false} />
              )}
            </DrillDownPanel>
          );
        })}
      </View>
    );
  };

  const renderCourses = (courses?: CourseProgressAnalytics[]) => {
    if (!courses || courses.length === 0) {
      return <CustomText text="No courses found." size="s" useTranslationText={false} />;
    }
    const activeCourses = courses.filter((course) => hasEngagement(course));

    if (activeCourses.length === 0) {
      return <CustomText text="No active courses found." size="s" useTranslationText={false} />;
    }

    const sorted = sortByEngagement(activeCourses);
    return (
      <View>
        {sorted.map((course) => (
          <DrillDownPanel
            key={course.id}
            label={course.label}
            item={course}
            type="course"
            level={0}
            defaultOpen={true} // Always open courses by default
            inactive={false}>
            {course.details?.modules && renderModules(course.details.modules)}
          </DrillDownPanel>
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToStudents}>
        <CustomText text="← Back to Students" size="m" useTranslationText={false} />
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.header}>
        <CustomText text={studentDetailedAnalyticsData.label} size="xl" weight="bold" useTranslationText={false} />
        <View
          style={[
            styles.sentimentBadge,
            {
              backgroundColor: getSentimentColor(studentDetailedAnalyticsData.sentiment) + "20",
              borderColor: getSentimentColor(studentDetailedAnalyticsData.sentiment),
            },
          ]}>
          <CustomText
            text={studentDetailedAnalyticsData.sentiment}
            size="s"
            style={{ color: getSentimentColor(studentDetailedAnalyticsData.sentiment) }}
            useTranslationText={false}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Key Metrics */}
      <View style={styles.metricsGrid}>
        <View style={styles.metricCard}>
          <CustomText
            text="Success Points"
            size="s"
            weight="bold"
            style={{ color: "#10B981" }}
            useTranslationText={false}
          />
          <CustomText
            text={`${studentDetailedAnalyticsData.success_points}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText
            text="Confusion Points"
            size="s"
            weight="bold"
            style={{ color: "#EF4444" }}
            useTranslationText={false}
          />
          <CustomText
            text={`${studentDetailedAnalyticsData.confusion_points}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText
            text="Questions Asked"
            size="s"
            weight="bold"
            style={{ color: "#3B82F6" }}
            useTranslationText={false}
          />
          <CustomText
            text={`${studentDetailedAnalyticsData.questions_asked}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText
            text="Total Time"
            size="s"
            weight="bold"
            style={{ color: "#8B5CF6" }}
            useTranslationText={false}
          />
          <CustomText
            text={formatTime(studentDetailedAnalyticsData.total_time_taken || 0)}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Overall Completion */}
      {studentDetailedAnalyticsData.completion_percentage !== undefined && (
        <View style={styles.completionSection}>
          <CustomText text="Overall Completion" size="l" weight="bold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text={formatPercentage(
              studentDetailedAnalyticsData.completion_percentage > 1
                ? studentDetailedAnalyticsData.completion_percentage
                : studentDetailedAnalyticsData.completion_percentage * 100
            )}
            size="m"
            useTranslationText={false}
          />
          <ProgressBar
            percentage={
              studentDetailedAnalyticsData.completion_percentage > 1
                ? studentDetailedAnalyticsData.completion_percentage
                : studentDetailedAnalyticsData.completion_percentage * 100
            }
          />
        </View>
      )}

      <CustomSpacing type="vertical" size="l" />

      {/* Drill-Down Section */}
      <View style={styles.drillDownSection}>
        <CustomText
          text="Drill Down: Courses, Modules, Activities, Objectives"
          size="l"
          weight="bold"
          useTranslationText={false}
        />
        <CustomText
          text="Click to expand each level for full analytics and progress details."
          size="s"
          useTranslationText={false}
        />

        <CustomSpacing type="vertical" size="m" />

        {renderCourses(studentDetailedAnalyticsData.details?.courses)}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  backButton: {
    alignSelf: "flex-start",
    padding: 8,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sentimentBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
  },
  metricsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: 120,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center",
  },
  completionSection: {
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  drillDownSection: {
    marginBottom: 20,
  },
  drillDownPanel: {
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 12,
  },
  panelHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  panelHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  expandIcon: {
    marginRight: 8,
    color: "#6B7280",
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    marginLeft: 8,
  },
  progressSection: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: "#E5E7EB",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 3,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  metricBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },
  panelContent: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  progressDetailsContainer: {
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginBottom: 12,
  },
  summarySection: {
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    marginBottom: 12,
  },
  pointsGrid: {
    flexDirection: "row",
    gap: 12,
  },
  pointsSection: {
    flex: 1,
    minWidth: 120,
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  progressDetailsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  levelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  analyticsMetricsSection: {
    flexDirection: "row",
    gap: 16,
  },
  analyticsColumn: {
    flex: 1,
  },
  badgesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 8,
  },
  analyticsBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  originalityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
    flex: 1,
  },
  metricRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  pointsHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  pointsDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  pointItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
    paddingLeft: 4,
  },
  pointBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 6,
    marginRight: 8,
    flexShrink: 0,
  },
});

export default StudentDetail;
